import React, {
	PropsWithChildren,
	memo,
	useCallback,
	useMemo,
	useState,
} from "react";
import { cloneDeep, isUndefined } from "lodash";
import { useTranslation } from "react-i18next";
import { Icon } from "uikit";
import styled from "styled-components";

import Order from "../../../../../../../../services/Order";
import PhoneBlacklist from "../../../../../../../../services/PhoneBlacklist";
import { useTypedSelector } from "../../../../../../../../redux/store";
import { useCheckFlagAccessControl } from "../../../../../../../../hooks";
import {
	Divider,
	StyledRow,
	StyledSpan,
	Button,
	AccessGuard,
} from "../../../../../../../../components/common";
import { Mark } from "../../../../../components";
import { useOrderModalContext } from "../../../../../context";
import CloseOrderModal from "../../../CloseOrderModal";
import CopyOrderModal from "../../../CopyOrderModal";
import { CloseReason } from "../../../CloseOrderModal/components/Content";
import AddClientToBlacklistModal from "../../../AddClientToBlacklistModal";
import ClientCancelOrderModal from "../../../ClientCancelOrderModal";
import { TabAccessNames, TabKeys } from "../../constants/access";

import { SearchFilter } from "./components/SearchFilter";
import SendingSMS from "./components/SendingSMS";
import { ClientCall } from "./components/ClientCall";
import { DriverCall } from "./components/DriverCall";
import { ButtonFooter } from "./styled";
import OrderPaymentStatusBadge from "../../../../../../../../components/OrderPaymentStatusBadge";
import { getOrderPaymentStatus } from "../../../../../../../../utils/orderPaymentStatus";

const BaseButton = styled(Button)`
	width: max-content;
	padding: 5px 20px;
	height: clamp(29px, 3.9vh, 32px);
`;

const Footer: React.FC<Footer.Props> = ({
	disabled = false,
	locked,
	save,
	cancel,
	close,
	onCloseTab,
	isLoadingBtnSubmit,
	paymentStatusOrder,
}) => {
	const { t } = useTranslation();
	const checkFlagAccessControl = useCheckFlagAccessControl(TabAccessNames);

	const hasPaymentStatus = Boolean(getOrderPaymentStatus(paymentStatusOrder));
	const paymentStatusLabel = t([
		"orderPageWidgets.orderTable.modelTable.columns.paymentStatus",
		"Payment status",
	]);

	const {
		companyID,
		taxiServiceId,
		activeOrderId,
		phone: clientPhone,
		phones: clientAdditionalPhones,
		fullName,
		executorPhone,
		executorPhones,
		orderStatus,
		searchTypes,
		setSearchTypes,
		searchToggle,
		setSearchToggle,
		getAdditionalFields,
		showCustomerPhone,
		setOrdersTypeTabs,
		scheduledTime,
		canWorkingWithOrder,
		transfer,
		isOwn,
		executorCallSign,
	} = useOrderModalContext();

	const { preliminaryOrderBecomesLive } = useTypedSelector(
		(state) => state.settings.order.preliminary,
	);
	const timeToLive = preliminaryOrderBecomesLive.interval; // TODO add more defaults from the settings filia

	const [closeOrderModalValue, setCloseOrderModalValue] =
		useState<CloseOrderModal.Value | null>(null);
	const [isClientCancelOrderModalOpen, setIsClientCancelOrderModalOpen] =
		useState(false);
	const [
		isAddClientToBlackListModalOpen,
		setIsAddClientToBlackListModalOpen,
	] = useState(false);
	const [isCloseOrderModalOpen, setIsCloseOrderModalOpen] = useState(false);
	const [isCloneOrderModalOpen, setIsCloneOrderModalOpen] = useState(false);

	const isDisabled = useMemo(
		() => disabled || canWorkingWithOrder,
		[canWorkingWithOrder, disabled],
	);

	const clientPhones = useMemo(() => {
		const payload: string[] = [];

		if (
			!isOwn &&
			transfer &&
			transfer?.lastReceivedOrder?.phones?.length &&
			executorCallSign &&
			orderStatus === "executing"
		) {
			[...transfer.lastReceivedOrder.phones]
				.sort((a, b) => a.group - b.group)
				.forEach((item) => payload.push(item?.number || ""));

			return payload;
		}

		if (clientPhone) payload.push(clientPhone);
		if (clientAdditionalPhones && clientAdditionalPhones.length) {
			payload.push(...clientAdditionalPhones);
		}
		return payload;
	}, [
		clientAdditionalPhones,
		clientPhone,
		executorCallSign,
		isOwn,
		orderStatus,
		transfer,
	]);

	const openCloneOrderModal = useCallback(() => {
		setIsCloneOrderModalOpen(true);
	}, []);
	const openCloseOrderModal = useCallback(() => {
		setIsCloseOrderModalOpen(true);
	}, []);
	const closeCloneOrderModal = useCallback(() => {
		setIsCloneOrderModalOpen(false);
	}, []);

	const closeCloseOrderModal = useCallback(() => {
		setIsCloseOrderModalOpen(false);
	}, []);

	const cloneOrderModalOnSubmit = useCallback(
		async (value: CopyOrderModal.Value) => {
			if (activeOrderId) Order.copy(activeOrderId, value);
			setIsCloneOrderModalOpen(false);
		},
		[activeOrderId],
	);

	const closeAddClientToBlacklistModal = useCallback(() => {
		setIsAddClientToBlackListModalOpen(false);
		setCloseOrderModalValue(null);
	}, []);

	const blockClient = useCallback(
		(value: AddClientToBlacklistModal.Value) => {
			if (clientPhone && companyID) {
				PhoneBlacklist.store({
					level: value.level,
					description: value.comment ?? "",
					phone: clientPhone,
					firstName: fullName || " ",
					companyIds: [companyID],
				});
			}
		},
		[fullName, clientPhone, companyID],
	);

	const closeClientCancelOrderModal = useCallback(() => {
		setIsClientCancelOrderModalOpen(false);
		setCloseOrderModalValue(null);
	}, []);

	const submitClientCancelOrderModal = useCallback(
		(value: ClientCancelOrderModal.Value) => {
			setIsClientCancelOrderModalOpen(false);
			setCloseOrderModalValue(null);
			if (!activeOrderId) return;
			Order.close(activeOrderId, {
				status: closeOrderModalValue?.reason,
				subStatus: value.reason,
				comment: value.comment,
			});

			onCloseTab(activeOrderId);

			if (closeOrderModalValue?.addClientToBlackList) {
				blockClient({
					level: value.blockLevel,
					comment: value.blockComment,
				});
			}
		},
		[
			activeOrderId,
			blockClient,
			closeOrderModalValue?.addClientToBlackList,
			closeOrderModalValue?.reason,
			onCloseTab,
		],
	);

	const submitAddClientToBlacklistModal = useCallback(
		(value: AddClientToBlacklistModal.Value) => {
			setIsAddClientToBlackListModalOpen(false);
			setCloseOrderModalValue(null);
			if (activeOrderId) {
				Order.close(activeOrderId, {
					status: closeOrderModalValue?.reason,
				});
				blockClient(value);
				onCloseTab(activeOrderId);
			}
		},
		[activeOrderId, blockClient, closeOrderModalValue?.reason, onCloseTab],
	);

	const submitCloseOrderModal = useCallback(
		(value: CloseOrderModal.Value) => {
			setIsCloseOrderModalOpen(false);

			if (!activeOrderId) return;

			if (
				value.reason === CloseReason.ClientCanceled ||
				value.addClientToBlackList
			) {
				setCloseOrderModalValue(value);

				if (value.reason === CloseReason.ClientCanceled) {
					setIsClientCancelOrderModalOpen(true);
				} else {
					setIsAddClientToBlackListModalOpen(true);
				}
			} else {
				Order.close(activeOrderId, {
					status: value.reason,
				});
				onCloseTab(activeOrderId);
			}
		},
		[activeOrderId, onCloseTab],
	);

	const onShowClientNumberToExecutor = useCallback(() => {
		if (isUndefined(activeOrderId)) return;
		const orderAdditionalFields = getAdditionalFields();
		const additionalFields = cloneDeep(orderAdditionalFields);
		additionalFields.displaySettings =
			additionalFields.displaySettings ?? {};
		additionalFields.displaySettings.showCustomerPhone =
			!additionalFields.displaySettings.showCustomerPhone;
		Order.update({ id: activeOrderId, additionalFields });
	}, [activeOrderId, getAdditionalFields]);

	const eventKeydownCancel = useCallback(
		(event: React.KeyboardEvent<HTMLButtonElement>) => {
			const keyEvent = event?.key;
			if (keyEvent === "Enter") {
				cancel();
				event?.stopPropagation();
				event?.preventDefault();
			}
			if (keyEvent === "Tab") {
				const nextElem = document.getElementById("main-phone-id");
				if (nextElem) nextElem.focus();
			}

			if (keyEvent === "ArrowRight") {
				const nextElem = document.getElementById("order-save-id");
				if (nextElem) nextElem.focus();
			}

			if (keyEvent === "ArrowLeft") {
				const nextElem = document.getElementById("customer-phone-id");
				if (nextElem) nextElem.focus();
			}
		},
		[cancel],
	);

	const eventKeydownSave = useCallback(
		(event: React.KeyboardEvent<HTMLButtonElement>) => {
			const keyEvent = event?.key;
			if (keyEvent === "Enter") {
				save();

				if (scheduledTime && scheduledTime > Date.now()) {
					setOrdersTypeTabs("preliminary");
				} else if (orderStatus === "creating") {
					setOrdersTypeTabs("live");
				}
				event?.stopPropagation();
				event?.preventDefault();
			}
			if (keyEvent === "Tab") {
				const nextElem = document.getElementById("main-phone-id");
				if (nextElem) nextElem.focus();
			}

			if (keyEvent === "ArrowRight") {
				const nextElem = document.getElementById("customer-phone-id");
				if (nextElem) nextElem.focus();
			}

			if (keyEvent === "ArrowLeft") {
				const nextElem = document.getElementById("driver-node-id");
				if (nextElem) nextElem.focus();
			}
		},
		[save, setOrdersTypeTabs, orderStatus, scheduledTime],
	);

	const onSave = useCallback(() => {
		save();

		const parsedOrderDate = scheduledTime
			? new Date(scheduledTime).getTime()
			: NaN;

		// timeToLive

		// Calculate the future time threshold based on timeToLive
		const futureTime = timeToLive ? Date.now() + timeToLive : null;

		const isMoreTime =
			parsedOrderDate && futureTime
				? parsedOrderDate > futureTime
				: false;

		if (isMoreTime) {
			setOrdersTypeTabs("preliminary");
		} else if (orderStatus === "creating") {
			setOrdersTypeTabs("live");
		}
	}, [save, scheduledTime, timeToLive, orderStatus, setOrdersTypeTabs]);

	return (
		<StyledRow
			h="clamp(37px, 5.6vh, 50px)"
			p="0 18px"
			alignItems="center"
			w="100%"
			flex={{ wrap: "nowrap" }}
		>
			<StyledRow gap="5px" flex={{ wrap: "nowrap" }} alignItems="center">
				{isCloneOrderModalOpen && activeOrderId && (
					<CopyOrderModal
						onClose={closeCloneOrderModal}
						onSubmit={cloneOrderModalOnSubmit}
					/>
				)}

				{isCloseOrderModalOpen && activeOrderId && (
					<CloseOrderModal
						onClose={closeCloseOrderModal}
						onSubmit={submitCloseOrderModal}
					/>
				)}

				{isAddClientToBlackListModalOpen && activeOrderId && (
					<AddClientToBlacklistModal
						onClose={closeAddClientToBlacklistModal}
						onSubmit={submitAddClientToBlacklistModal}
					/>
				)}

				{isClientCancelOrderModalOpen && activeOrderId && (
					<ClientCancelOrderModal
						addClientToBlackList={
							closeOrderModalValue?.addClientToBlackList ?? false
						}
						onClose={closeClientCancelOrderModal}
						onSubmit={submitClientCancelOrderModal}
					/>
				)}

				<AccessGuard accessName={TabAccessNames[TabKeys.CLOSED_ORDER]}>
					<ButtonFooter
						disabled={
							orderStatus === "creating" ||
							orderStatus === "closed" ||
							isDisabled
						}
						variant="secondary"
						settingsKeyId="orderModalCloseOrder"
						title={
							t(
								"pages.mainPage.pages.orders.orderModal.footer.str0",
							) ?? ""
						}
						onClick={openCloseOrderModal}
					>
						<Icon id="orders-close" size={24} />
					</ButtonFooter>
				</AccessGuard>

				<AccessGuard accessName={TabAccessNames[TabKeys.COPY_ORDER]}>
					<ButtonFooter
						disabled={
							orderStatus === "creating" || isDisabled
								? orderStatus !== "closed"
								: isDisabled
						}
						variant="secondary"
						settingsKeyId="orderModalCopyOrder"
						title={
							t(
								"pages.mainPage.pages.orders.orderModal.footer.str1",
							) ?? ""
						}
						onClick={openCloneOrderModal}
					>
						<Icon id="orders-copy" size={24} />
					</ButtonFooter>
				</AccessGuard>
				<Divider h="24px" />
				<AccessGuard accessName={TabAccessNames[TabKeys.SMS_BUTTON]}>
					<SendingSMS
						disabled={
							isDisabled ? orderStatus !== "closed" : isDisabled
						}
						mainPhone={clientPhone}
						phones={clientPhones}
						taxiServiceId={taxiServiceId}
					/>
				</AccessGuard>
				<AccessGuard
					accessName={TabAccessNames[TabKeys.CALL_ORDER_CLIENT]}
				>
					<ClientCall
						disabled={
							isDisabled ? orderStatus !== "closed" : isDisabled
						}
						mainPhone={clientPhone}
						phones={clientPhones}
						orderId={activeOrderId}
					/>
				</AccessGuard>
				<AccessGuard
					accessName={TabAccessNames[TabKeys.CALL_ORDER_EXECUTOR]}
				>
					<DriverCall
						disabled={
							isDisabled ? orderStatus !== "closed" : isDisabled
						}
						mainPhone={executorPhone}
						phones={executorPhones}
						orderId={activeOrderId}
						settingsKeyId="orderModalCallOrderExecutor"
					/>
				</AccessGuard>
				<AccessGuard
					accessName={
						TabAccessNames[TabKeys.SHOW_CLIENT_NUMBER_TO_EXECUTOR]
					}
				>
					<ButtonFooter
						disabled={orderStatus === "creating" || isDisabled}
						active={showCustomerPhone}
						variant="secondary"
						settingsKeyId="orderModalShowPhoneToExecutor"
						title={
							t(
								"pages.mainPage.pages.orders.orderModal.footer.str2",
							) ?? ""
						}
						onClick={onShowClientNumberToExecutor}
					>
						<Icon
							id="orders-show-client-number-to-executor"
							size={20}
						/>
					</ButtonFooter>
				</AccessGuard>

				<Divider h="24px" />

				<SearchFilter
					disabled={isDisabled}
					toggles={searchToggle}
					onChangeToggle={setSearchToggle}
					setSearchType={setSearchTypes}
					activeSearchTypes={searchTypes}
				/>
				<Divider h="24px" />
				<BaseButton
					disabled={
						isDisabled || !checkFlagAccessControl[TabKeys.CLOSE_ALL]
					}
					title={t([`close_all`, "Close all tabs"]) ?? ""}
					variant="secondary"
					onClick={close}
				>
					{t([`close_all`, "Close all tabs"]) ?? ""}
				</BaseButton>
			</StyledRow>

			<StyledRow
				flex={{ wrap: "nowrap", grow: 1 }}
				gap="15px"
				justify="start"
				alignItems="center"
				p="0 1rem"
			>
				{hasPaymentStatus && (
					<StyledSpan>
						{paymentStatusLabel}:{" "}
						<OrderPaymentStatusBadge order={paymentStatusOrder} />
					</StyledSpan>
				)}
				{locked && (
					<>
						<Divider h="18px" m="0 10px" />
						<StyledSpan>
							{t([
								"order_editing",
								`The order is already edited by the user -`,
							])}
							<Mark>{`${locked?.name}`}</Mark>
						</StyledSpan>
					</>
				)}
			</StyledRow>

			<StyledRow gap="13px" flex={{ wrap: "nowrap" }}>
				<BaseButton
					onKeyDown={eventKeydownCancel}
					title={t([`cancel`, "Cancel"]) ?? ""}
					id="order-cancel-id"
					settingsKeyId="orderModalCancelOrder"
					variant="secondary"
					onClick={cancel}
					disabled={!checkFlagAccessControl[TabKeys.CANCEL]}
					css={`
						:focus {
							border-radius: 6px;
							border: 1px solid #21333f;
						}
					`}
				>
					{t([`cancel`, "Cancel"]) ?? ""}
				</BaseButton>

				<BaseButton
					onKeyDown={eventKeydownSave}
					id="order-save-id"
					onClick={onSave}
					disabled={
						isDisabled || !checkFlagAccessControl[TabKeys.SAVE]
					}
					tabIndex={11}
					title={t([`save`, "Save"]) ?? ""}
					settingsKeyId="orderModalEditOrder"
					variant="primary"
					css={`
						:focus {
							border-radius: 6px;
							border: 1px solid #21333f;
						}
					`}
					loading={isLoadingBtnSubmit}
				>
					{t([`save`, "Save"]) ?? ""}
				</BaseButton>
			</StyledRow>
		</StyledRow>
	);
};

declare namespace Footer {
	interface Props extends PropsWithChildren {
		locked?: any;
		disabled?: boolean;
		save: () => void;
		cancel: () => void;
		close: () => void;
		onCloseTab: (orderId: number) => void;
		isLoadingBtnSubmit: boolean;
		/** The card order (live subscription): its payment status is shown. */
		paymentStatusOrder?: Parameters<typeof getOrderPaymentStatus>[0];
	}
}

export const FooterMemo = memo(Footer);
export default Footer;
